import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { getHttpClient, HttpClient, useHttpClient } from '@bonusly/http-client';
import { getQueryClient } from '@bonusly/query-client';

import { UserVote, VotablePeriodSummary, VotingView } from '../schemas/nomination-voting';
import { listOf } from '../schemas/utils';

enum QueryKey {
	VotingView = 'nominations:voting-view',
	VotablePeriods = 'nominations:votable-periods',
}

export const nominationVotingViewQueryKey = (id: string) => [QueryKey.VotingView, id];

export const votablePeriodsQueryKey = () => [QueryKey.VotablePeriods];

export const nominationVotingPeriodPath = (id: string, prefix = '') => `${prefix}/nomination_voting_periods/${id}`;

export const nominationVotablePeriodsPath = (prefix = '') => `${prefix}/nomination_voting_periods`;

export const nominationVotesPath = (id: string, prefix = '') => `${prefix}/nomination_voting_periods/${id}/votes`;

// Frontend route paths (where the voter navigates), distinct from the API paths above.
export const nominationVotingIndexRoutePath = () => '/vote/nominations';

export const nominationVotingPageRoutePath = (id: string) => `/vote/nominations/${id}`;

const createNominationVotingViewFetcher = (http: HttpClient, id: string) => async () => {
	const res = await http.get(nominationVotingPeriodPath(id), {
		headers: {
			'Key-Inflection': 'camel',
		},
	});

	return VotingView.parse(res.data);
};

export const useNominationVotingView = (id: string) => {
	return useQuery({
		queryKey: nominationVotingViewQueryKey(id),
		queryFn: createNominationVotingViewFetcher(useHttpClient(), id),
	});
};

export const loadNominationVotingView = async (id: string) => {
	const http = await getHttpClient();
	const queryClient = await getQueryClient();

	return queryClient.fetchQuery({
		queryKey: nominationVotingViewQueryKey(id),
		queryFn: createNominationVotingViewFetcher(http, id),
	});
};

export const useStoreNominationVotingView = () => {
	const queryClient = useQueryClient();

	return useCallback(
		(view: VotingView) => {
			queryClient.setQueryData<VotingView>(nominationVotingViewQueryKey(view.nominationPeriodId), view);
		},
		[queryClient]
	);
};

export const useFetchNominationVotingView = () => {
	const queryClient = useQueryClient();

	return useCallback((id: string) => queryClient.getQueryData<VotingView>(nominationVotingViewQueryKey(id)), [queryClient]);
};

const createVotablePeriodsFetcher = (http: HttpClient) => async () => {
	try {
		const res = await http.get(nominationVotablePeriodsPath(), {
			headers: {
				'Key-Inflection': 'camel',
			},
		});

		return listOf(VotablePeriodSummary).parse(res.data);
	} catch (e) {
		// The home-page banner polls this on every load. A 403 means the company
		// isn't eligible for nomination voting (e.g. not on the required plan),
		// which is an empty result rather than an error worth surfacing to React
		// Query / RUM (INS-1195). The banner simply stays hidden.
		if ((e as { status?: number })?.status === 403) {
			return [];
		}

		throw e;
	}
};

export const useVotablePeriods = (options: { enabled?: boolean } = {}) => {
	return useQuery({
		queryKey: votablePeriodsQueryKey(),
		queryFn: createVotablePeriodsFetcher(useHttpClient()),
		enabled: options.enabled,
	});
};

export const loadVotablePeriods = async () => {
	const http = await getHttpClient();
	const queryClient = await getQueryClient();

	return queryClient.fetchQuery({
		queryKey: votablePeriodsQueryKey(),
		queryFn: createVotablePeriodsFetcher(http),
	});
};

type CastVotesOptions = {
	onError?: (error: unknown) => void;
	onSuccess?: (votes: UserVote[]) => void;
};

/**
 * Casts (replaces) a voter's votes: send the full set of selected nominee
 * uniqueness keys, and the server will destroy the voter's existing votes for
 * the period and create the new set in one logical operation. Pass an empty
 * array to clear all votes.
 *
 * The server returns only the voter's votes (casting never changes the nominee
 * list or anonymized responses), so we merge them into the cached view's
 * `userVotes` rather than refetching the whole view.
 */
export const useCastVotes = (id: string, options: CastVotesOptions = {}) => {
	const http = useHttpClient();
	const store = useStoreNominationVotingView();
	const fetchView = useFetchNominationVotingView();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (nomineeUniquenessKeys: string[]) => {
			try {
				const res = await http.put(
					nominationVotesPath(id),
					{ nomineeUniquenessKeys },
					{
						headers: {
							'Key-Inflection': 'camel',
						},
					}
				);

				const userVotes = listOf(UserVote).parse(res.data);
				const existing = fetchView(id);
				if (existing) {
					store({ ...existing, userVotes });
				}
				// Keep the votable-periods list (banner + index page) in sync with the
				// vote the user just cast/cleared, so the index "Voted" status and the
				// home banner reflect it without waiting for a refetch.
				queryClient.setQueryData<VotablePeriodSummary[]>(votablePeriodsQueryKey(), (prev) =>
					prev?.map((period) => (period.id === id ? { ...period, hasVoted: userVotes.length > 0 } : period))
				);
				options.onSuccess?.(userVotes);
				return userVotes;
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};
